import { useEffect, useRef, useState } from 'react';
import type { RepoRef } from '../types/environment';

interface OrgOption {
	org: string;
}

interface Props {
    orgs: OrgOption[];
    selectedRepos: RepoRef[];
    setSelectedRepos: (repos: RepoRef[]) => void;
    environmentID: string;
}

export default function RepoSelector({ orgs, selectedRepos, setSelectedRepos, environmentID }: Props) {
	const [selectedOrg, setSelectedOrg] = useState<OrgOption | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<string[]>([]);
    const [highlight, setHighlight] = useState(-1);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const totalRepos = selectedRepos.length;

    useEffect(() => {
        setSelectedOrg(orgs[0]);
    }, [orgs]);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                clearSearch();
            }
        }
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    useEffect(() => {
        if (highlight >= 0 && listRef.current) {
            const el = listRef.current.children[highlight] as HTMLElement | undefined;
            el?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight]);

    const clearSearch = () => {
        abortRef.current?.abort();
        setSearch('');
        setOptions([]);
        setHighlight(-1);
        setOpen(false);
        setLoading(false);
    };

    const closeMenu = () => {
        abortRef.current?.abort();
        setOptions([]);
        setHighlight(-1);
        setOpen(false);
        setLoading(false);
    };

    const updateRepoSearch = async (q: string) => {
        abortRef.current?.abort();
        setSearch(q);
        if (!q || !selectedOrg || totalRepos >= 100) {
            setOptions([]);
            setOpen(false);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setOpen(true);
		try {
			const resp = await fetch(
				`/api/github/repos?environmentID=${encodeURIComponent(environmentID)}&org=${encodeURIComponent(selectedOrg.org)}&q=${encodeURIComponent(q)}`,
				{ signal: controller.signal },
			);
            if (resp.ok) {
                const data: string[] = await resp.json();
                const repoNames = data
                    .filter((r) => r.startsWith(`${selectedOrg.org}/`))
                    .map((r) => r.slice(selectedOrg.org.length + 1))
                    .filter((r) => !selectedRepos.some((sr) => sr.Org === selectedOrg.org && sr.Repo === r));
                setOptions(repoNames);
                setHighlight(repoNames.length > 0 ? 0 : -1);
            } else {
                setOptions([]);
            }
        } catch (err) {
            if ((err as any).name !== 'AbortError') {
                console.error('search repos error', err);
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const addRepo = (repo: string) => {
        if (!repo || totalRepos >= 100 || !selectedOrg) {
            return;
        }
        if (selectedRepos.some((r) => r.Org === selectedOrg.org && r.Repo === repo)) {
            return;
        }
		setSelectedRepos([...selectedRepos, { Org: selectedOrg.org, Repo: repo }]);
        clearSearch();
    };

    const pageJump = (dir: 1 | -1) => {
        if (!listRef.current) {
            setHighlight(dir === 1 ? options.length - 1 : 0);
            return;
        }
        const container = listRef.current;
        const item = container.firstElementChild as HTMLElement | null;
        const itemHeight = item?.offsetHeight || 1;
        const visibleCount = Math.floor(container.clientHeight / itemHeight) || options.length;
        setHighlight((h) => {
            if (options.length <= visibleCount) {
                return dir === 1 ? options.length - 1 : 0;
            }
            if (h < 0) {
                return dir === 1 ? visibleCount : 0;
            }
            if (dir === 1) {
                const next = Math.floor(h / visibleCount) * visibleCount + visibleCount;
                return Math.min(next, options.length - 1);
            } else {
                const next = Math.floor(h / visibleCount) * visibleCount - 1;
                return Math.max(next, 0);
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, options.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'PageDown') {
            e.preventDefault();
            pageJump(1);
        } else if (e.key === 'PageUp') {
            e.preventDefault();
            pageJump(-1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight >= 0 && options[highlight]) {
                addRepo(options[highlight]);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closeMenu();
        }
    };

    const removeRepo = (idx: number) => {
        setSelectedRepos(selectedRepos.filter((_, i) => i !== idx));
    };

    return (
        <div ref={containerRef}>
            <div>GitHub repositories</div>
            {selectedRepos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                    {selectedRepos.map((r, idx) => (
                        <div
                            key={idx}
                            style={{
                                padding: '4px 8px',
                                background: '#eee',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            {r.Org}/{r.Repo}
                            <button type="button" className="btn-icon" onClick={() => removeRepo(idx)}>
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ marginTop: '4px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                    value={selectedOrg?.org || ''}
                    onChange={(ev) => setSelectedOrg(orgs.find((o) => o.org === ev.target.value))}
                    style={{ padding: '6px' }}
                >
                    {orgs.map((o) => (
                        <option key={o.org} value={o.org}>
                            {o.org}
                        </option>
                    ))}
                </select>
                <span>/</span>
                <div style={{ flex: 1, position: 'relative' }}>
                    <input
                        value={search}
                        onChange={(ev) => updateRepoSearch(ev.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ width: '100%', padding: '6px' }}
                    />
                    {open && (
                        options.length > 0 ? (
                            <div
                                ref={listRef}
                                style={{
                                    border: '1px solid #ccc',
                                    position: 'absolute',
                                    background: '#fff',
                                    zIndex: 1,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                }}
                            >
                                {options.map((r, idx) => (
                                    <div
                                        key={r}
                                        style={{
                                            padding: '4px',
                                            cursor: 'pointer',
                                            background: idx === highlight ? '#def' : undefined,
                                        }}
                                        onMouseEnter={() => setHighlight(idx)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => addRepo(r)}
                                    >
                                        {r}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            !loading && (
                                <div
                                    style={{
                                        border: '1px solid #ccc',
                                        position: 'absolute',
                                        background: '#fff',
                                        padding: '4px',
                                    }}
                                >
                                    No results
                                </div>
                            )
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
